// src/lib/state/connection-recovery.state.svelte.ts
// ====================================================================
// CONNECTION RECOVERY STATE MACHINE
// ====================================================================
//
// SINGLE SOURCE OF TRUTH for: bootstrap status, transport health,
// auth validity, data freshness, and recovery orchestration.
//
// Before this module, truth was spread across appState, connectionState,
// healthState, websocketState, and layout-local `forceReady` flags.
// Services emitted ad-hoc window events (`ws:prolonged-failure`,
// `health:backend-recovered`) with no owner. Dashboards ran hidden 15s
// timers that flipped `isLoading=false` to "empty success" — the exact
// "0 0 0 0" ghost state the user complained about.
//
// This machine replaces all of that with a disciplined reducer.
//
// --------------------------------------------------------------------
// ARCHITECTURE
// --------------------------------------------------------------------
// - Reducer pattern: services dispatch typed `Signal`s. The machine
//   computes Phase + RecoveryLevel + ErrorTaxonomy and owns every
//   recovery decision.
// - `Phase = CONNECTED` means "fresh data proved," NOT "socket opened."
//   Transport flipping to open is a *signal*, not a state.
// - Every timer is explicit, named, and cancelable. No hidden setTimeouts.
// - No silent catch-and-swallow. Unknown conditions escalate the ladder.
// - All navigation to `/login` is triggered from here, never from
//   services. Services emit signals; the machine decides.
//
// --------------------------------------------------------------------
// CURRENT SCOPE (Phase 1 + Phase 2 wired)
// --------------------------------------------------------------------
// - Phases INITIALIZING, CONNECTED, DEGRADED, RECONNECTING,
//   UNRECOVERABLE, LOGIN_REQUIRED are fully implemented and driven.
// - CONNECTING / RECOVERING are defined but not yet transitioned into
//   (Phase 3/4 will activate them alongside REST fallback and L5 reset).
// - REST fallback (L3) and in-place re-bootstrap (L5) are declared on
//   the ladder; actual REST polling lives in Phase 3.
// - Services (WebSocket, DataSync, Health, AppInit, Auth) dispatch
//   directly into the machine via `notify*` / `recoverySignal*`
//   wrappers. The only observer remaining is the auth state observer.
// ====================================================================

import { browser } from '$app/environment';
import { SvelteDate } from 'svelte/reactivity';
import { domainState } from '$lib/state/index.svelte';
import { isAuthReady, isAuthenticated, tokenState } from '$lib/state/auth.state.svelte';
import { setLayerStatus as setHealthLayerStatus } from '$lib/state/health.state.svelte';
import { createLogger } from '$lib/utils/logger';

const log = createLogger('recovery');

// ====================================================================
// TYPES
// ====================================================================

/**
 * Connection recovery phase.
 *
 * The user sees exactly one of these at any time. Each phase owns
 * its own UI contract (skeletons, banners, full-screen overlays).
 */
export type Phase =
	| 'INITIALIZING'       // bootstrap running, no data yet
	| 'CONNECTING'         // auth valid, WS connecting, awaiting snapshot (P2 active)
	| 'CONNECTED'          // fresh snapshot received, live channel healthy
	| 'DEGRADED'           // data present but live pipeline unhealthy (stale/REST)
	| 'RECONNECTING'       // previously-healthy WS lost, retrying
	| 'RECOVERING'         // running L3/L4/L5 recovery actions (P2 active)
	| 'UNRECOVERABLE'      // no trusted data path; full-screen error, Retry/Sign out
	| 'LOGIN_REQUIRED';    // definitive auth failure; navigate to /login

/**
 * Recovery ladder. Lower numbers = gentler. Higher = more destructive.
 *
 * The machine picks a level based on the current error taxonomy and
 * history. It never skips levels except for definitive auth failure.
 */
export type RecoveryLevel =
	| 'L0_NONE'          // no recovery in progress
	| 'L1_TRANSIENT'     // single request/heartbeat retry, silent
	| 'L2_WS_RECONNECT'  // WebSocket reconnect loop
	| 'L3_REST_FALLBACK' // HTTP polling as backup data source
	| 'L4_TOKEN_REFRESH' // only on confirmed auth failures, not network blips
	| 'L5_FULL_RESET'    // in-place re-bootstrap, no browser refresh
	| 'L6_NUCLEAR';      // clear storage, goto /login

/**
 * Error taxonomy. Each symptom maps to a category, each category maps
 * to a recovery ladder entry point.
 */
export type ErrorTaxonomy =
	| 'NONE'
	| 'TRANSIENT_NETWORK'            // L1 → L2
	| 'BACKEND_SLOW'                 // L1 → L2 → L3
	| 'BACKEND_DOWN'                 // L2/L3; UNRECOVERABLE if no trusted path
	| 'TOKEN_EXPIRED_REFRESHABLE'    // L4
	| 'TOKEN_EXPIRED_UNRECOVERABLE'  // L6 → LOGIN_REQUIRED
	| 'PERMISSION_REVOKED'           // L6 → LOGIN_REQUIRED
	| 'DATA_PIPELINE_BROKEN'         // L5 first
	| 'BOOTSTRAP_TIMEOUT'            // INITIALIZING exceeded budget → UNRECOVERABLE
	| 'UNKNOWN';

/**
 * Data source. Tracks which pipeline is currently providing data so the
 * UI can show "Live" / "Cached (2 min ago)" / "Backup polling".
 */
export type DataSource =
	| 'none'          // no data yet
	| 'cache'         // localStorage snapshot
	| 'live'          // fresh WebSocket snapshot
	| 'rest_fallback'; // REST polling (L3)

/**
 * Backend-reported data quality. Mirrors the `data_status` field the
 * backend includes in every DATA_SNAPSHOT message (see
 * amos-backend-linux/app/core/websocket/data_quality.py). Only 'fresh'
 * is safe to treat as CONNECTED; everything else triggers DEGRADED.
 */
export type DataQualityStatus =
	| 'fresh'              // live, complete, trustworthy
	| 'stale'              // older than expected, backend cache-served
	| 'site_data_missing'  // alerts present but asset enrichment failed (partial DB)
	| 'empty_cache'        // backend returned empty because its own cache was empty
	| 'unavailable'        // backend couldn't reach the external database
	| 'unknown';

/**
 * Typed signals dispatched into the reducer. Each signal is idempotent —
 * the reducer guards against duplicate transitions.
 */
export type Signal =
	| { type: 'BOOT_START' }
	| { type: 'AUTH_READY'; authenticated: boolean }
	| { type: 'AUTH_INVALID'; reason: 'token_expired' | 'permission_revoked' | 'unknown' }
	| { type: 'WS_OPEN' }
	| { type: 'WS_CLOSE'; abnormal: boolean }
	| { type: 'WS_RECONNECTING'; attempt: number }
	| { type: 'WS_RECONNECTED' }
	| { type: 'WS_PROLONGED_FAILURE' }
	| { type: 'SNAPSHOT_RECEIVED'; source: DataSource; dataStatus: DataQualityStatus }
	| { type: 'HEALTH_HEALTHY' }
	| { type: 'HEALTH_DEGRADED' }
	| { type: 'DATA_PIPELINE_BROKEN' }
	| { type: 'USER_RETRY' }
	| { type: 'USER_SIGN_OUT' }
	| { type: 'BOOTSTRAP_TIMEOUT' }
	// P14: Staleness timer fires when HEALTH_DEGRADED sits for longer
	// than STALENESS_THRESHOLD_MS without a fresh snapshot arriving.
	// Transitions CONNECTED → DEGRADED. Previously the threshold
	// constant existed but was never consumed — the machine would
	// sit in CONNECTED indefinitely with `degradedSince` set but no
	// user-visible banner, because nothing translated "health says
	// backend is sick AND no fresh data for 45s" into a phase change.
	| { type: 'STALENESS_EXCEEDED' };

/**
 * Machine state. Shape is stable across phases — readers derive what
 * they need via the exported selectors below.
 */
export interface RecoveryMachineState {
	phase: Phase;
	level: RecoveryLevel;
	taxonomy: ErrorTaxonomy;
	dataSource: DataSource;

	/** Last time fresh LIVE data arrived. null if never. */
	lastGoodLiveAt: Date | null;

	/** Last time ANY data arrived (live, cache, or REST). null if never. */
	lastGoodAnyDataAt: Date | null;

	/** When the current degradation started (for banners / timeouts). */
	degradedSince: Date | null;

	/** Current recovery attempt count within the active level. */
	activeAttempt: number;

	/** Human-readable reason for the current phase. Null when CONNECTED. */
	error: string | null;

	/** Whether a user-facing Retry action is currently valid. */
	canRetry: boolean;

	/** True in LOGIN_REQUIRED — layout reads this to navigate. */
	loginRequired: boolean;
}

// ====================================================================
// TIMERS
// ====================================================================
//
// Every timer is named and lives here. No anonymous setTimeouts in
// the reducer. Calling a `start*` twice is safe — it cancels the
// previous instance. All timers are cleared on transition to any
// non-degraded phase or explicit `clearAllTimers()`.
// ====================================================================

/** Total bootstrap budget: if INITIALIZING doesn't reach a data phase in 15s, escalate. */
const BOOTSTRAP_BUDGET_MS = 15_000;

/** Initial data budget: once auth+WS are ready, how long until first snapshot. */
const INITIAL_DATA_BUDGET_MS = 10_000;

/** How long the app can be RECONNECTING before escalating to REST fallback (P3). */
const RECONNECT_CUMULATIVE_BUDGET_MS = 30_000;

/** How stale is "too stale" for DEGRADED display. */
const STALENESS_THRESHOLD_MS = 45_000;

const timers = new Map<string, ReturnType<typeof setTimeout>>();

function startTimer(name: string, delay: number, fn: () => void): void {
	const existing = timers.get(name);
	if (existing) clearTimeout(existing);
	timers.set(
		name,
		setTimeout(() => {
			timers.delete(name);
			fn();
		}, delay)
	);
}

function clearTimer(name: string): void {
	const t = timers.get(name);
	if (t) {
		clearTimeout(t);
		timers.delete(name);
	}
}

function clearAllTimers(): void {
	for (const t of timers.values()) clearTimeout(t);
	timers.clear();
}

function hasTimer(name: string): boolean {
	return timers.has(name);
}

// P14 follow-up: explicit module-private flag tracking whether the
// backend health signal is currently degraded. Needed because:
//
//   - HEALTH_DEGRADED fires every poll while backend is sick, not just
//     on the healthy→unhealthy edge. startTimer() would reset the 45s
//     window on every re-dispatch (advisor finding: timer never fires).
//   - SNAPSHOT_RECEIVED (fresh live) clears the staleness timer. If
//     the backend is still degraded, we must RE-ARM the timer or a
//     prolonged degraded period with occasional fresh snapshots would
//     never trip STALENESS_EXCEEDED (codex finding).
//
// The reducer reads/writes this flag inside HEALTH_DEGRADED / HEALTH_HEALTHY
// and the SNAPSHOT_RECEIVED fresh-live branch consults it to decide
// whether to re-arm.
let backendHealthDegraded = false;

// ====================================================================
// STATE (reactive)
// ====================================================================

const initialState: RecoveryMachineState = {
	phase: 'INITIALIZING',
	level: 'L0_NONE',
	taxonomy: 'NONE',
	dataSource: 'none',
	lastGoodLiveAt: null,
	lastGoodAnyDataAt: null,
	degradedSince: null,
	activeAttempt: 0,
	error: null,
	canRetry: false,
	loginRequired: false
};

export const recoveryState = $state<RecoveryMachineState>({ ...initialState });

// ====================================================================
// REDUCER
// ====================================================================
//
// Pure transition logic. Takes the current state and a signal,
// mutates `recoveryState` in place (Svelte 5 idiom). Every transition
// logs its before/after for traceability.
//
// Invariants:
//   - CONNECTED requires at least one SNAPSHOT_RECEIVED since bootstrap
//   - LOGIN_REQUIRED is sticky: once set, only USER_SIGN_OUT or a full
//     BOOT_START resets it (prevents flapping during navigation races)
//   - UNRECOVERABLE can only be exited via USER_RETRY or USER_SIGN_OUT
// ====================================================================

/**
 * Dispatch a signal into the machine. Module-private — external callers
 * must go through the named wrappers below (recoveryBootStart,
 * recoverySignalAuthInvalid, userRetry, userSignOut). This prevents
 * anyone from sending raw USER_RETRY / BOOT_START from outside.
 */
function dispatch(signal: Signal): void {
	const before = recoveryState.phase;
	apply(signal);
	const after = recoveryState.phase;

	if (before !== after) {
		log.info(`phase: ${before} → ${after}`, {
			signal: signal.type,
			level: recoveryState.level,
			taxonomy: recoveryState.taxonomy,
			dataSource: recoveryState.dataSource
		});
	} else {
		log.debug(`signal: ${signal.type} (stayed ${before})`);
	}
}

function apply(signal: Signal): void {
	// LOGIN_REQUIRED is sticky — only reset by explicit reboot or sign out.
	if (recoveryState.phase === 'LOGIN_REQUIRED') {
		if (signal.type === 'BOOT_START' || signal.type === 'USER_SIGN_OUT') {
			// fall through to handler
		} else {
			return;
		}
	}

	// UNRECOVERABLE only exits via user action or a fresh snapshot.
	if (recoveryState.phase === 'UNRECOVERABLE') {
		if (
			signal.type === 'USER_RETRY' ||
			signal.type === 'USER_SIGN_OUT' ||
			signal.type === 'SNAPSHOT_RECEIVED' ||
			signal.type === 'BOOT_START'
		) {
			// fall through
		} else {
			return;
		}
	}

	switch (signal.type) {
		// ----- bootstrap -----
		case 'BOOT_START': {
			Object.assign(recoveryState, initialState);
			// This flag lives outside recoveryState, so Object.assign above does
			// NOT clear it. Left stale at `true`, a health blip from a previous
			// session would make the next WebSocket-only failure claim the
			// backend is unreachable on no current evidence.
			backendHealthDegraded = false;
			clearAllTimers();
			recoveryState.phase = 'INITIALIZING';
			recoveryState.canRetry = false;
			startTimer('bootstrap', BOOTSTRAP_BUDGET_MS, () => dispatch({ type: 'BOOTSTRAP_TIMEOUT' }));
			return;
		}

		case 'AUTH_READY': {
			if (!signal.authenticated) {
				recoveryState.phase = 'LOGIN_REQUIRED';
				recoveryState.taxonomy = 'TOKEN_EXPIRED_UNRECOVERABLE';
				recoveryState.level = 'L6_NUCLEAR';
				recoveryState.loginRequired = true;
				recoveryState.error = 'Session expired. Please sign in again.';
				recoveryState.canRetry = false;
				clearAllTimers();
				return;
			}
			// auth valid → keep bootstrap timer running until SNAPSHOT_RECEIVED
			return;
		}

		case 'AUTH_INVALID': {
			recoveryState.phase = 'LOGIN_REQUIRED';
			recoveryState.loginRequired = true;
			recoveryState.taxonomy =
				signal.reason === 'permission_revoked'
					? 'PERMISSION_REVOKED'
					: 'TOKEN_EXPIRED_UNRECOVERABLE';
			recoveryState.level = 'L6_NUCLEAR';
			recoveryState.error =
				signal.reason === 'permission_revoked'
					? 'Your permissions were revoked. Please sign in again.'
					: 'Session expired. Please sign in again.';
			recoveryState.canRetry = false;
			clearAllTimers();
			return;
		}

		// ----- websocket lifecycle -----
		case 'WS_OPEN': {
			// Socket is open, but NOT yet CONNECTED. Wait for snapshot.
			// Start the "initial data" budget from now — we allow 10s for the
			// backend to send DATA_SNAPSHOT before escalating.
			if (recoveryState.phase === 'INITIALIZING' || recoveryState.phase === 'CONNECTING') {
				startTimer('initial-data', INITIAL_DATA_BUDGET_MS, () => {
					if (
						recoveryState.phase === 'INITIALIZING' ||
						recoveryState.phase === 'CONNECTING'
					) {
						// No snapshot in 10s — treat as pipeline stall.
						dispatch({ type: 'DATA_PIPELINE_BROKEN' });
					}
				});
			}
			return;
		}

		case 'WS_CLOSE': {
			// Only React to a real connected→closed transition. Ignore the
			// transient "close during connect" that naturally fires during
			// reconnect loops.
			if (recoveryState.phase === 'CONNECTED' || recoveryState.phase === 'DEGRADED') {
				if (recoveryState.lastGoodAnyDataAt) {
					// We have data — keep it on screen, flip to RECONNECTING.
					recoveryState.phase = 'RECONNECTING';
					recoveryState.taxonomy = 'TRANSIENT_NETWORK';
					recoveryState.level = 'L2_WS_RECONNECT';
					recoveryState.degradedSince = new SvelteDate();
					recoveryState.error = 'Connection lost — reconnecting';
					startTimer('reconnect-budget', RECONNECT_CUMULATIVE_BUDGET_MS, () => {
						// P3 will wire REST fallback here. For now: escalate taxonomy.
						if (recoveryState.phase === 'RECONNECTING') {
							recoveryState.taxonomy = 'BACKEND_DOWN';
							recoveryState.level = 'L3_REST_FALLBACK';
							// Record what we are actually showing. The sibling
							// WS_PROLONGED_FAILURE branch already does this;
							// without it the banner said "showing cached data"
							// while dataSource still claimed 'live', so the copy
							// and the state disagreed about the same moment.
							recoveryState.dataSource = 'cache';
							// Only the WebSocket is known to have failed here.
							recoveryState.error = messageForDegradedChannel('cache', backendHealthDegraded);
							recoveryState.phase = 'DEGRADED';
						}
					});
				} else {
					// No data yet — this is a bootstrap failure path.
					dispatch({ type: 'DATA_PIPELINE_BROKEN' });
				}
			}
			return;
		}

		case 'WS_RECONNECTING': {
			recoveryState.activeAttempt = signal.attempt;
			if (
				recoveryState.phase === 'CONNECTED' ||
				recoveryState.phase === 'INITIALIZING'
			) {
				recoveryState.phase = recoveryState.lastGoodAnyDataAt ? 'RECONNECTING' : 'INITIALIZING';
			}
			return;
		}

		case 'WS_RECONNECTED': {
			// Transport reconnected but we still need a fresh snapshot to
			// return to CONNECTED. Stay in RECONNECTING until data arrives.
			recoveryState.activeAttempt = 0;
			return;
		}

		case 'WS_PROLONGED_FAILURE': {
			// 2 minutes of failed reconnects. P3 will activate REST fallback.
			if (recoveryState.lastGoodAnyDataAt) {
				recoveryState.phase = 'DEGRADED';
				recoveryState.taxonomy = 'BACKEND_DOWN';
				recoveryState.level = 'L3_REST_FALLBACK';
				recoveryState.dataSource = 'cache';
				recoveryState.error = messageForDegradedChannel('cache', backendHealthDegraded);
			} else {
				recoveryState.phase = 'UNRECOVERABLE';
				recoveryState.taxonomy = 'BACKEND_DOWN';
				recoveryState.level = 'L5_FULL_RESET';
				recoveryState.error =
					'Cannot reach the backend server after multiple attempts. The server may be down or restarting.';
				recoveryState.canRetry = true;
			}
			return;
		}

		// ----- data lifecycle -----
		case 'SNAPSHOT_RECEIVED': {
			// A REST fallback snapshot must NEVER downgrade a healthy live
			// connection. Defends the HTTP-first bootstrap race where the
			// one-shot loadRestFallbackOnce() resolves in the same tick the
			// live socket delivers its first snapshot (and the generation guard
			// on the continuous poller). Live data wins; ignore the REST one.
			if (signal.source === 'rest_fallback' && recoveryState.phase === 'CONNECTED') {
				return;
			}

			const now = new SvelteDate();
			recoveryState.lastGoodAnyDataAt = now;
			recoveryState.dataSource = signal.source;

			// If the backend marked this snapshot as degraded, refuse to
			// treat it as CONNECTED — even if the source is 'live'. This
			// is the P4 fix for the "0 0 0 0 = success" ghost state.
			//
			// Backend sends data_status ∈ {stale, empty_cache, unavailable,
			// site_data_missing} when the external DB is sick or the data
			// is questionable. Only these explicit values count as degraded.
			//
			// `undefined` and `unknown` both mean "caller didn't tell us",
			// which must NOT trip into degraded — DataSyncService defaults
			// to 'unknown' when the caller omits the arg, and we used to
			// flag that as degraded, which sent every snapshot-without-
			// dataStatus straight to L3 on bootstrap.
			const explicitlyDegraded = new Set([
				'stale',
				'empty_cache',
				'unavailable',
				'site_data_missing',
			]);
			const isDegradedStatus =
				signal.dataStatus !== undefined && explicitlyDegraded.has(signal.dataStatus);

			if (signal.source === 'live' && !isDegradedStatus) {
				// Happy path: fresh live data → CONNECTED
				recoveryState.lastGoodLiveAt = now;
				recoveryState.phase = 'CONNECTED';
				recoveryState.taxonomy = 'NONE';
				recoveryState.level = 'L0_NONE';
				recoveryState.error = null;
				// Only clear degradedSince if the backend health signal
				// agrees we're healthy. If health is still degraded we
				// keep the degradation start timestamp for banner display
				// and we re-arm the staleness timer below.
				if (!backendHealthDegraded) {
					recoveryState.degradedSince = null;
				}
				recoveryState.activeAttempt = 0;
				recoveryState.canRetry = false;
				clearTimer('bootstrap');
				clearTimer('initial-data');
				clearTimer('reconnect-budget');
				// P14: fresh data arrived — reset the staleness timer.
				// If health is still degraded, re-arm it immediately so
				// a prolonged unhealthy period with occasional fresh
				// snapshots still trips STALENESS_EXCEEDED when the
				// snapshots stop arriving (codex finding).
				clearTimer('staleness');
				if (backendHealthDegraded) {
					startTimer('staleness', STALENESS_THRESHOLD_MS, () =>
						dispatch({ type: 'STALENESS_EXCEEDED' })
					);
				}
			} else if (signal.source === 'live' && isDegradedStatus) {
				// Live snapshot but backend flagged it degraded. This is
				// the DB-sick / cache-empty / stale case. Machine stays
				// in DEGRADED with an explicit layer-specific message.
				recoveryState.phase = 'DEGRADED';
				recoveryState.taxonomy = 'BACKEND_DOWN';
				recoveryState.level = 'L3_REST_FALLBACK';
				recoveryState.error = messageForDataStatus(signal.dataStatus);
				recoveryState.degradedSince ||= now;
				recoveryState.canRetry = true;
				// Clear the bootstrap/initial-data timers — we DID receive
				// a response, it's just a degraded one. The user will see
				// the banner and can choose to Retry.
				clearTimer('bootstrap');
				clearTimer('initial-data');
			} else if (signal.source === 'rest_fallback') {
				recoveryState.phase = 'DEGRADED';
				recoveryState.taxonomy = 'BACKEND_DOWN';
				recoveryState.level = 'L3_REST_FALLBACK';
				// A REST snapshot just arrived over HTTP — do NOT claim the
				// backend is unreachable, because we just reached it.
				recoveryState.error = messageForDegradedChannel('rest_fallback', backendHealthDegraded);
				recoveryState.degradedSince ||= now;
				// Offer a manual retry in the DEGRADED banner, matching the
				// live-degraded and staleness branches.
				recoveryState.canRetry = true;
				// A REST snapshot during bootstrap (HTTP-first path) makes the
				// app usable, so the bootstrap/initial-data budgets are moot —
				// clear them so a late BOOTSTRAP_TIMEOUT can't fire spuriously.
				// Also clear the reconnect budget: if we entered here from
				// RECONNECTING, that timer would otherwise fire as a no-op and
				// linger in the timers map.
				clearTimer('bootstrap');
				clearTimer('initial-data');
				clearTimer('reconnect-budget');
			}
			return;
		}

		case 'HEALTH_HEALTHY': {
			backendHealthDegraded = false;
			// Health check says backend is up. We do NOT transition to
			// CONNECTED from here — that would be optimistic without
			// proof of live data. Instead, the recovery path is:
			//   1. Health says OK → poller stays/goes to NORMAL_INTERVAL
			//   2. WebSocket keeps sending DATA_TICK events
			//   3. handleTick dispatches SNAPSHOT_RECEIVED with source='live'
			//   4. That handler owns the transition to CONNECTED
			// The only thing we do here is clear the BACKEND_DOWN taxonomy
			// so the banner copy updates while we wait for live proof.
			if (recoveryState.phase === 'DEGRADED' || recoveryState.phase === 'RECONNECTING') {
				if (recoveryState.taxonomy === 'BACKEND_DOWN') {
					recoveryState.taxonomy = 'TRANSIENT_NETWORK';
					// Re-resolve rather than hardcoding. This branch fires on
					// every healthy poll while DEGRADED, so a fixed string here
					// would overwrite the evidence-graded message the moment a
					// health check came back OK — reasserting "the backend was
					// down" precisely when we have proof it is not. The flag is
					// already false at this point (set above).
					//
					// Only the backup-data sources get the channel copy. A
					// 'live' dataSource here means a live snapshot the BACKEND
					// flagged as degraded (stale/empty_cache/...), where
					// messageForDataStatus already said something specific and
					// "showing cached data" would be plainly wrong.
					if (
						recoveryState.dataSource === 'rest_fallback' ||
						recoveryState.dataSource === 'cache'
					) {
						recoveryState.error = messageForDegradedChannel(
							recoveryState.dataSource,
							backendHealthDegraded
						);
					} else {
						recoveryState.error = 'Backend recovering — restoring live connection';
					}
				}
			}
			// P14: backend is healthy again — cancel any pending staleness
			// timer so a recovered service doesn't later trip us into
			// DEGRADED for a race that already resolved.
			clearTimer('staleness');
			if (recoveryState.phase === 'CONNECTED') {
				recoveryState.degradedSince = null;
			}
			return;
		}

		case 'HEALTH_DEGRADED': {
			backendHealthDegraded = true;
			if (recoveryState.phase === 'CONNECTED') {
				// Start tracking degradation and arm the staleness timer.
				// We don't leave CONNECTED immediately — a brief health
				// blip is tolerable if fresh data keeps flowing. The
				// SNAPSHOT_RECEIVED (fresh live) handler clears this
				// timer. If no fresh snapshot arrives within
				// STALENESS_THRESHOLD_MS, STALENESS_EXCEEDED fires and
				// moves the machine to DEGRADED so the user sees the
				// banner instead of silently sitting on stale data.
				if (!recoveryState.degradedSince) {
					recoveryState.degradedSince = new SvelteDate();
				}
				// CRITICAL (advisor finding): only arm the timer if it
				// is not already running. The health poller re-dispatches
				// HEALTH_DEGRADED on every poll interval (not just on
				// edges), so an unconditional startTimer would reset the
				// 45s clock every 30s and the timer would never fire.
				if (!hasTimer('staleness')) {
					startTimer('staleness', STALENESS_THRESHOLD_MS, () =>
						dispatch({ type: 'STALENESS_EXCEEDED' })
					);
				}
			}
			return;
		}

		case 'STALENESS_EXCEEDED': {
			// P14: armed by HEALTH_DEGRADED. Only meaningful if we're
			// still in CONNECTED — if a fresh snapshot already arrived
			// the SNAPSHOT_RECEIVED handler cleared the timer, and if
			// something else already moved us out of CONNECTED the
			// signal is a no-op.
			if (recoveryState.phase === 'CONNECTED') {
				recoveryState.phase = 'DEGRADED';
				recoveryState.taxonomy = 'BACKEND_DOWN';
				recoveryState.level = 'L3_REST_FALLBACK';
				recoveryState.error =
					'Backend health degraded and no fresh data for 45 seconds — showing last-known values';
				recoveryState.degradedSince ||= new SvelteDate();
				recoveryState.canRetry = true;
			}
			return;
		}

		case 'DATA_PIPELINE_BROKEN': {
			// Transport is fine, auth is fine, but data is not flowing.
			// P2 wires L5 full reset here. For now: UNRECOVERABLE so the
			// user sees an honest error and can retry.
			recoveryState.phase = 'UNRECOVERABLE';
			recoveryState.taxonomy = 'DATA_PIPELINE_BROKEN';
			recoveryState.level = 'L5_FULL_RESET';
			recoveryState.error =
				'The data pipeline is not responding. This usually resolves itself — click Retry to restart.';
			recoveryState.canRetry = true;
			clearAllTimers();
			return;
		}

		// ----- user actions -----
		case 'USER_RETRY': {
			// Reset the machine and kick off a fresh bootstrap. The layout
			// observes phase=INITIALIZING and re-runs appInitializer.
			clearAllTimers();
			Object.assign(recoveryState, initialState);
			// Module-level, so not covered by the assign above — see BOOT_START.
			backendHealthDegraded = false;
			recoveryState.phase = 'INITIALIZING';
			startTimer('bootstrap', BOOTSTRAP_BUDGET_MS, () =>
				dispatch({ type: 'BOOTSTRAP_TIMEOUT' })
			);
			return;
		}

		case 'USER_SIGN_OUT': {
			// Flip to LOGIN_REQUIRED — layout handles storage clear + navigation.
			recoveryState.phase = 'LOGIN_REQUIRED';
			recoveryState.loginRequired = true;
			recoveryState.level = 'L6_NUCLEAR';
			recoveryState.taxonomy = 'NONE';
			recoveryState.error = null;
			recoveryState.canRetry = false;
			clearAllTimers();
			return;
		}

		case 'BOOTSTRAP_TIMEOUT': {
			if (recoveryState.phase === 'INITIALIZING') {
				recoveryState.phase = 'UNRECOVERABLE';
				recoveryState.taxonomy = 'BOOTSTRAP_TIMEOUT';
				recoveryState.level = 'L5_FULL_RESET';
				recoveryState.error =
					'The application could not start within 15 seconds. The backend may be slow or unreachable.';
				recoveryState.canRetry = true;
				clearAllTimers();
			}
			return;
		}
	}
}

// ====================================================================
// OBSERVERS (Phase 2: minimal — services dispatch directly)
// ====================================================================
//
// Phase 2 eliminated most observers. Services now import the named
// `notify*` wrappers and call them directly:
//
//   - WebSocket transport → notifyRecoveryWsOpen/Close/ProlongedFailure
//   - DataSyncService     → notifyRecoverySnapshot
//   - health.api.ts       → notifyRecoveryHealth
//   - appInitializer      → notifyRecoveryBootstrapFailed
//   - AuthService/layout  → recoverySignalAuthInvalid, userSignOut, etc.
//
// The ONLY remaining observer watches auth state. Auth involves several
// reactive flags (ready, authenticated, isRefreshing) that cooperate
// in a way that's cleaner to read as derived state than to intercept
// at every mutation site. Keeping one auth observer is the pragmatic
// compromise — the rest are gone.
// ====================================================================

let observersInstalled = false;
let observersCleanup: (() => void) | null = null;

export function installObservers(): void {
	if (!browser || observersInstalled) return;
	observersInstalled = true;

	observersCleanup = $effect.root(() => {
		// Auth observer: maps (ready, authenticated, refreshing) to
		// AUTH_READY signals. Suppresses signals while a refresh is in
		// flight so the machine doesn't flap to LOGIN_REQUIRED on
		// transient state during token refresh.
		$effect(() => {
			const ready = isAuthReady();
			const authed = isAuthenticated();
			const refreshing = tokenState.isRefreshing;

			if (ready && !refreshing) {
				dispatch({ type: 'AUTH_READY', authenticated: authed });
			}
		});

		return () => {
			// Nothing to clean up — the $effect.root's disposal covers
			// the inner $effect automatically.
		};
	});

	log.info('Recovery observers installed');
}

/**
 * Tear down observers. Call on logout or full app teardown.
 */
export function uninstallObservers(): void {
	if (observersCleanup) {
		observersCleanup();
		observersCleanup = null;
	}
	observersInstalled = false;
	clearAllTimers();
	log.info('Recovery observers uninstalled');
}

// ====================================================================
// SELECTORS (for UI components)
// ====================================================================

/** True while skeletons should render. */
export function isBootstrapping(): boolean {
	return recoveryState.phase === 'INITIALIZING' || recoveryState.phase === 'CONNECTING';
}

/** True while UI can render real data. */
export function canRenderData(): boolean {
	return (
		recoveryState.phase === 'CONNECTED' ||
		recoveryState.phase === 'DEGRADED' ||
		recoveryState.phase === 'RECONNECTING'
	);
}

/** True while the full-screen error overlay should be visible. */
export function showUnrecoverableOverlay(): boolean {
	return recoveryState.phase === 'UNRECOVERABLE';
}

/** True when the layout should navigate to /login. */
export function mustRedirectToLogin(): boolean {
	return recoveryState.phase === 'LOGIN_REQUIRED';
}

/** Banner visibility + variant for DataHealthBanner. */
export function getBannerState(): {
	visible: boolean;
	variant: 'blue' | 'amber' | 'red';
	title: string;
	message: string;
} {
	switch (recoveryState.phase) {
		case 'RECONNECTING':
			return {
				visible: true,
				variant: 'blue',
				title: 'Reconnecting',
				message: recoveryState.lastGoodLiveAt
					? `Connection lost — showing data from ${formatRelativeTime(recoveryState.lastGoodLiveAt)}. Reconnecting automatically.`
					: 'Connection lost. Reconnecting automatically.'
			};
		case 'DEGRADED':
			return {
				visible: true,
				variant: 'amber',
				title:
					recoveryState.dataSource === 'rest_fallback'
						? 'Backup Polling Active'
						: 'Live Updates Paused',
				message:
					recoveryState.error ??
					'The live data channel is temporarily unavailable. Data will resume automatically.'
			};
		case 'INITIALIZING':
		case 'CONNECTING':
			// Banner suppressed during initial load — skeletons carry the message.
			return { visible: false, variant: 'blue', title: '', message: '' };
		default:
			return { visible: false, variant: 'blue', title: '', message: '' };
	}
}

function formatRelativeTime(d: Date): string {
	const secs = Math.floor((Date.now() - d.getTime()) / 1000);
	if (secs < 60) return `${secs}s ago`;
	const mins = Math.floor(secs / 60);
	if (mins < 60) return `${mins} min ago`;
	const hours = Math.floor(mins / 60);
	return `${hours}h ago`;
}

// ====================================================================
// PUBLIC SIGNAL WRAPPERS
// ====================================================================
//
// Callers outside this module never invoke `dispatch()` directly.
// They use named wrappers that document intent and restrict the
// signal surface. This prevents "anyone can USER_RETRY from anywhere"
// anti-patterns.
// ====================================================================

/**
 * Begin a protected-page bootstrap. Called once by the layout when
 * the app transitions into an authenticated protected-page context.
 * Idempotent via the BOOT_START reducer which resets state.
 */
export function recoveryBootStart(): void {
	dispatch({ type: 'BOOT_START' });
}

/**
 * Signal that the current session is definitively invalid (token
 * refresh failed, permissions revoked, etc). Called by AuthService
 * and by the layout's auth event handlers. Transitions the machine
 * to LOGIN_REQUIRED, which the layout observes and navigates on.
 */
export function recoverySignalAuthInvalid(
	reason: 'token_expired' | 'permission_revoked' | 'unknown' = 'unknown'
): void {
	dispatch({ type: 'AUTH_INVALID', reason });
}

/**
 * Data layer signals that a snapshot landed. This is the SOLE
 * AUTHORITATIVE trigger for CONNECTED / DEGRADED phase transitions.
 *
 * Phase 4: dataStatus is now MANDATORY in the signal. If the backend
 * flags the data as anything other than 'fresh', the machine routes
 * to DEGRADED instead of CONNECTED, surfacing the exact reason in the
 * banner. Previously the machine ignored data_status and would happily
 * show a "successful" empty dashboard when the database was sick.
 *
 * Callers:
 *   DataSyncService.handleSnapshot → passes the data_status from the
 *     WebSocket DATA_SNAPSHOT payload
 *   RestFallbackPoller.pollOnce    → passes 'stale' (REST is always stale
 *     by definition; the source='rest_fallback' forces DEGRADED anyway)
 */
export function notifyRecoverySnapshot(
	source: 'live' | 'rest_fallback' = 'live',
	dataStatus: DataQualityStatus = 'fresh'
): void {
	dispatch({ type: 'SNAPSHOT_RECEIVED', source, dataStatus });
}

/**
 * Banner copy for a degraded LIVE CHANNEL, graded by what we can actually
 * prove at the moment it is written.
 *
 * This exists because three call sites used to hardcode "Backend unreachable"
 * with wildly different evidence behind them. On 2026-07-20 a user saw that
 * message while HTTP was perfectly healthy — only the WebSocket handshake was
 * failing, because the alerts bootstrap cache had gone cold and the connect
 * path was stalling on a 90s financial query. She concluded the system was
 * down and stopped trying to work. The banner must never claim more than the
 * state machine knows.
 *
 * Evidence ladder:
 *  - rest_fallback: a REST snapshot JUST arrived over HTTP, so HTTP reads are
 *    proven working. We still do not claim writes succeed — a read proves
 *    nothing about POST — only that each action is confirmed on its own.
 *    (True since alert actions reconcile on confirmation and surface an
 *    explicit error otherwise; see state/alerts.actions.svelte.ts.)
 *  - cache + health not degraded: we only know the WEBSOCKET failed. Say
 *    exactly that and make no claim about the backend or about actions —
 *    `backendHealthDegraded === false` can also mean "never reported", so it
 *    is not positive proof of health.
 *  - cache + health degraded: the health probe is actively failing, so
 *    "unreachable" is earned.
 */
function messageForDegradedChannel(
	source: 'rest_fallback' | 'cache',
	healthDegraded: boolean
): string {
	if (source === 'rest_fallback') {
		return 'Live updates paused — refreshing on a timer. You can still act on alerts; each action is confirmed separately.';
	}
	return healthDegraded
		? 'Backend unreachable — showing cached data. Retrying automatically.'
		: 'Live connection lost — showing cached data. Reconnecting automatically.';
}

/**
 * Human-readable banner message for each degraded data-status value.
 * Specific enough that the user knows which layer is broken.
 */
function messageForDataStatus(status: DataQualityStatus): string {
	switch (status) {
		case 'unavailable':
			return 'Database unavailable — cannot load fresh data. We will keep retrying automatically.';
		case 'empty_cache':
			return 'Backend returned no data — the database may be unreachable or still warming up.';
		case 'site_data_missing':
			return 'Partial data: alerts loaded but site details are missing. Database enrichment failed.';
		case 'stale':
			return 'Showing slightly stale data — the live connection is temporarily degraded.';
		case 'unknown':
			return 'Data quality unknown — the backend did not report a status.';
		default:
			return 'Data pipeline is degraded.';
	}
}

/**
 * WebSocket transport signals that the socket opened. Distinct from
 * CONNECTED — a fresh snapshot is still required before the machine
 * transitions out of CONNECTING.
 */
export function notifyRecoveryWsOpen(): void {
	dispatch({ type: 'WS_OPEN' });
	// 3-layer health — mirror the transport state into the health badge.
	setHealthLayerStatus('websocket', 'healthy', 'Connected');
}

/**
 * WebSocket transport signals an abnormal close (network, server,
 * transport error — NOT auth failure). Auth-failure close codes
 * (4001, 4003) should call `recoverySignalAuthInvalid` instead.
 */
export function notifyRecoveryWsClose(): void {
	dispatch({ type: 'WS_CLOSE', abnormal: true });
	setHealthLayerStatus('websocket', 'degraded', 'Connection lost — reconnecting');
}

/**
 * WebSocket transport signals prolonged failure (e.g. 2+ minutes of
 * failed reconnects). Machine decides whether to activate REST fallback
 * or transition to UNRECOVERABLE.
 */
export function notifyRecoveryWsProlongedFailure(): void {
	dispatch({ type: 'WS_PROLONGED_FAILURE' });
	// Says only what has happened. REST fallback may not have delivered
	// anything yet at this point (the reducer sets dataSource='cache'), so
	// claiming "using backup polling" here overstates it.
	setHealthLayerStatus('websocket', 'down', 'Unable to reconnect — showing cached data');
}

/**
 * Health API signals backend healthy / degraded. The machine uses this
 * to update banner text and to decide whether a DEGRADED state should
 * auto-clear.
 */
export function notifyRecoveryHealth(healthy: boolean): void {
	dispatch({ type: healthy ? 'HEALTH_HEALTHY' : 'HEALTH_DEGRADED' });
}

/**
 * appInitializer signals that the bootstrap sequence hit an
 * unrecoverable error (e.g. a required service threw). Machine
 * transitions to UNRECOVERABLE with a Retry button.
 */
export function notifyRecoveryBootstrapFailed(): void {
	dispatch({ type: 'DATA_PIPELINE_BROKEN' });
}

// ====================================================================
// USER ACTIONS (called from UI)
// ====================================================================

/**
 * User clicked "Retry" on the UnrecoverableErrorOverlay.
 * Re-bootstraps the app in place — no browser refresh.
 *
 * Resets `domainState.alerts.initialized` and `workOrders.initialized`
 * to false before re-init. Now that SNAPSHOT_RECEIVED flows directly
 * from DataSyncService (not observers), these flags are still useful
 * signals for dashboards and downstream UI to re-render skeletons
 * during the retry.
 */
export async function userRetry(): Promise<void> {
	log.info('User triggered retry — re-bootstrapping in place');

	// Reset data-initialized flags so downstream skeletons re-appear
	// during the retry. The machine itself no longer depends on these
	// for SNAPSHOT_RECEIVED — that signal flows directly from the
	// data layer in handleSnapshot.
	domainState.alerts.initialized = false;
	domainState.workOrders.initialized = false;

	dispatch({ type: 'USER_RETRY' });

	try {
		const { initializeApp } = await import('$lib/services/app/appInitializer');
		const ok = await initializeApp();
		if (!ok) {
			dispatch({ type: 'DATA_PIPELINE_BROKEN' });
		}
	} catch (err) {
		log.error('Retry bootstrap failed', {
			error: err instanceof Error ? err.message : String(err)
		});
		dispatch({ type: 'DATA_PIPELINE_BROKEN' });
	}
}

/**
 * User clicked "Sign out" on the UnrecoverableErrorOverlay or elsewhere.
 *
 * CRITICAL ORDER: clear storage BEFORE dispatching USER_SIGN_OUT. The
 * dispatch flips the machine to LOGIN_REQUIRED, which the layout's
 * $effect observes and immediately runs safeGoto('/login'). Previously
 * this ran in the wrong order — redirect could fire before storage was
 * cleared, leaving stale tokens in sessionStorage for the next session.
 *
 * EventBusService does not await async handlers, so `await` at the
 * handler level is not enough — we must clear synchronously relative
 * to the dispatch.
 */
export async function userSignOut(): Promise<void> {
	log.info('User triggered sign-out from recovery machine');

	// 1. Clear browser storage FIRST — sessionStorage is sync.
	if (browser) {
		sessionStorage.clear();
	}

	// 2. Clear secureStorage synchronously if the module is already
	//    loaded (it will be in any authenticated context). Dynamic
	//    import is a fallback for cases where it is not yet resolved.
	try {
		const mod = await import('$lib/utils/secure-storage');
		mod.secureStorage.clearAll();
	} catch (err) {
		log.warn('Failed to clear secure storage', {
			error: err instanceof Error ? err.message : String(err)
		});
	}

	// 3. NOW dispatch — layout redirect happens only after storage is clean.
	dispatch({ type: 'USER_SIGN_OUT' });
}

// ====================================================================
// DEBUG
// ====================================================================

if (browser && import.meta.env.DEV) {
	// DEV-only debug surface. `dispatch` is intentionally omitted — even
	// in DEV, callers should use the named wrappers so behavior is the
	// same as in production.
	(globalThis as any).__AMOS_RECOVERY__ = {
		state: recoveryState,
		userRetry,
		userSignOut,
		recoveryBootStart,
		recoverySignalAuthInvalid,
		timers: () => Array.from(timers.keys())
	};
}
